import Box from "@mui/material/Box";
import Paper from "@mui/material/Paper";
import ListItemText from "@mui/material/ListItemText";
import Stack, { StackProps } from "@mui/material/Stack";
import { format } from "date-fns";
import { useResponsive } from "src/hooks/use-responsive";

import Iconify from "src/components/iconify";
import { useSnackbar } from "src/components/snackbar";

import {
  Alert,
  Badge,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  List,
  ListItem,
  Typography,
} from "@mui/material";
import { t } from "i18next";
import { useCountdownDate } from "src/hooks/use-countdown";
import { displayPriceCredit } from "src/utils/format-number";
import { paths } from "src/routes/paths";
import { RouterLink } from "src/routes/components";
import {
  useGetActiveCartTransaction,
} from "src/api/transaction";
import { useTransaction } from "../checkout/context";
import { useEffect, useState } from "react";
import { getTimezoneAdjustedDate } from "src/utils/format-time";
// ----------------------------------------------------------------------

interface Props extends StackProps {
  transaction: any;
  onDelete: (id: number) => void;
  onClose: () => void;
}

export default function CartItem({
  transaction,
  onDelete,
  onClose,
  sx,
  ...other
}: Props) {
  const { enqueueSnackbar } = useSnackbar();
  const smUp = useResponsive("up", "sm");

  const {
    activeTransaction,
    setActiveTransaction,
    needToRefreshCart,
    setNeedToRefreshCart,
  } = useTransaction();

  const {
    id,
    type,
    price,
    providerUrltag,
    currency,
    credit,
    transactionitemsNumber,
    deadline,
    providerName,
    eventName,
  } = transaction;

  // const deadlineDate = new Date(
  //   moment.tz(deadline, "Europe/Budapest").format("YYYY-MM-DD HH:mm:ss")
  // );

  const deadlineDate = getTimezoneAdjustedDate(deadline, "plus1");

  //const now = moment().tz("Europe/Budapest").toDate();
  const now = getTimezoneAdjustedDate(new Date(), "plus1");
  const { minutes, seconds } = useCountdownDate(deadlineDate.toString());
  //const { minutes, seconds } = useCountdownDate(new Date(deadline));

  useEffect(() => {
    if (id && deadlineDate < now) {
      // Perform deletion if deadline has passed
      // deleteCartTransaction(id)
      //   .then(() => {
      //     enqueueSnackbar("Transaction deleted because the deadline expired.", {
      //       variant: "info",
      //     });
      //     if (activeTransaction?.id === id) {
      //       setActiveTransaction({});
      //     }
      //     setNeedToRefreshCart(true); // Trigger a refresh if needed
      //   })
      //   .catch((error) => {
      //     console.error("Error deleting transaction after deadline:", error);
      //   });
    }
  }, [
    id,
    deadlineDate,
    now,
    activeTransaction,
    setActiveTransaction,
    enqueueSnackbar,
    setNeedToRefreshCart,
  ]);

  const [open, setOpen] = useState(false);

  // ✅ Run the hook at the top level, enable it only when dialog is open
  const {
    transaction: transactionDetail,
    transactionLoading,
    transactionError,
    mutate, // if you need manual refresh
  } = useGetActiveCartTransaction(id, open);

  const handleOpenDialog = () => setOpen(true);
  const handleCloseDialog = () => setOpen(false);

  const renderRow = now < deadlineDate;

  const renderName = eventName ?? type;
  const renderText = (
    <ListItemText
      primary={
        <>
          <Box
            gap={3}
            display="grid"
            gridTemplateColumns={{
              xs: "repeat(1, 1fr)",
              sm: "repeat(2, 1fr)",
              md: "repeat(4, 1fr)",
            }}
          >
            <Box>{providerName}</Box>
            <Box>
              <u>{t(renderName)}</u>
              <Badge
                sx={{ ml: 1.5 }}
                anchorOrigin={{
                  vertical: "top",
                  horizontal: "right",
                }}
                badgeContent={transactionitemsNumber}
                color="info"
              ></Badge>
            </Box>
            <Box>
              <Stack
                direction="row"
                justifyContent="left"
                divider={<Box sx={{ mx: { xs: 0.5, sm: 0.5 } }}></Box>}
                sx={{ pt: 0, typography: "subtitle2" }}
              >
                <Iconify
                  icon="solar:clock-circle-bold"
                  color="primary.main"
                ></Iconify>
                <TimeBlock label={t("counter_minutes")} value={minutes} /> :
                <TimeBlock label={t("counter_seconds")} value={seconds} />
              </Stack>
            </Box>
            <Box>{displayPriceCredit(price, currency, credit)}</Box>
          </Box>
        </>
      }
      secondary={""}
      primaryTypographyProps={{
        mt: 2,
        noWrap: true,
        typography: "subtitle2",
      }}
      secondaryTypographyProps={{
        mt: 0.5,
        component: "span",
        alignItems: "center",
        typography: "caption",
        color: "text.disabled",
        display: "inline-flex",
      }}
    />
  );

  const renderAction = (
    <Box
      sx={{
        top: 0,
        right: 8,
        position: "absolute",
        ...(smUp && {
          flexShrink: 0,
          position: "unset",
        }),
      }}
    >
      {/* <IconButton color={"default"}>
        <Iconify icon="eva:more-vertical-fill" />
      </IconButton> */}

      <Button
        color="warning"
        variant="contained"
        startIcon={<Iconify icon="solar:trash-bin-trash-bold" />}
        sx={{ mr: 1, mt: { xs: 1, md: 0 } }}
        size="medium"
        onClick={(e) => {
          e.stopPropagation();
          onDelete(id);
        }}
      >
        {t("delete")}
      </Button>
      {transaction?.type && transaction.type !== "reservationperiod" && (
        <Button
          component={RouterLink}
          variant="contained"
          color="primary"
          size="medium"
          onClick={(e) => {
            e.stopPropagation();
            onClose();
          }}
          sx={{ mt: { xs: 1, md: 0 } }}
          startIcon={<Iconify icon="mingcute:add-line" />}
          href={
            paths.sportprovider.calendar(providerUrltag) + "?transaction=" + id
          }
        >
          {t("add")}
        </Button>
      )}
    </Box>
  );

  return (
    renderRow && (
      <>
        <Stack
          component={Paper}
          variant="outlined"
          spacing={1}
          onClick={handleOpenDialog}
          direction={{ xs: "column", sm: "row" }}
          alignItems={{ xs: "unset", sm: "center" }}
          sx={{
            borderRadius: 1,
            bgcolor: "unset",
            cursor: "pointer",
            position: "relative",
            p: { xs: 2.5, sm: 2 },
            "&:hover": {
              bgcolor: "background.paper",
              boxShadow: (theme) => theme.customShadows.z20,
            },
            ...sx,
          }}
          {...other}
        >
          <br />
          {renderText}
          {renderAction}
          <Button
            variant="contained"
            color="secondary"
            size="large"
            component={RouterLink}
            href={paths.checkout + "/?transaction=" + id + "&step=0"}
            onClick={(e) => {
              e.stopPropagation();
              onClose();
            }}
          >
            {t("checkout")}
          </Button>
        </Stack>

        {/* Details Dialog */}
        <Dialog
          fullWidth
          open={open}
          onClose={handleCloseDialog}
          aria-labelledby={`transaction-dialog-title-${id}`}
          scroll="paper"
        >
          <DialogTitle id={`transaction-dialog-title-${id}`} sx={{ pr: 6 }}>
            {t("details")}
            <IconButton
              aria-label={t("close")}
              onClick={handleCloseDialog}
              sx={{ position: "absolute", right: 8, top: 8 }}
            >
              <Iconify icon="mingcute:close-line" />
            </IconButton>
          </DialogTitle>

          <DialogContent dividers>
            {transactionLoading && (
              <Stack direction="row" alignItems="center" spacing={1}>
                <CircularProgress size={20} />
                <Typography variant="body2">{t("loading")}...</Typography>
              </Stack>
            )}

            {transactionError && (
              <Alert severity="error">
                {t("failed_to_load_transaction_details")}
              </Alert>
            )}

            {!transactionLoading && !transactionError && transactionDetail && (
              <Stack spacing={2}>
                <List dense>
                  {(transactionDetail.transactionitems ?? []).map(
                    (item: any, index: number) => (
                      <ListItem key={item.id ?? index} disableGutters>
                        <ListItemText
                          primary={renderTransactionItemLabel(item, index)}
                          primaryTypographyProps={{
                            typography: "body2",
                            noWrap: true,
                          }}
                        />
                      </ListItem>
                    )
                  )}
                </List>
              </Stack>
            )}
          </DialogContent>

          <DialogActions>
            <Stack spacing={3} direction={"row"}>
              <Button onClick={handleCloseDialog} variant="outlined">
                {t("close")}
              </Button>
              <Button
                variant="contained"
                color="secondary"
                component={RouterLink as any}
                href={`${paths.checkout}/?transaction=${id}&step=0`}
                onClick={() => {
                  // dialog closes naturally by route change; keep for safety:
                  handleCloseDialog();
                }}
              >
                {t("checkout")}
              </Button>
            </Stack>
          </DialogActions>
        </Dialog>
      </>
    )
  );
}

type TimeBlockProps = {
  label: string;
  value: string;
};

function TimeBlock({ label, value }: TimeBlockProps) {
  return (
    <div>
      <Box>
        {value}
        {/* {label} */}
      </Box>
    </div>
  );
}

const renderTransactionItemLabel = (item: any, index: number) => {
  const prefix = `(${index + 1}) `;
  const price = displayPriceCredit(
    Number(item?.price || 0),
    item?.currency || "",
    Number(item?.credit || 0)
  );
  const discount = item?.discount_name
    ? ` • ${t("discount")}: ${item.discount_name}`
    : "";

  if (item?.item_type === "tournamentapplication" && item?.application) {
    return `(${index + 1}) ${
      item.application.category_name || "Tournament application"
    } • ${price}${discount}${
      item.application.tournament_name
        ? ` • ${item.application.tournament_name}`
        : ""
    }`;
  }

  if (item?.reservation?.start_date && item?.reservation?.end_date) {
    return (
      prefix +
      [
        item?.reservation?.event_name ?? "",
        format(new Date(item.reservation.start_date), "yyyy MMM dd"),
        `${format(new Date(item.reservation.start_date), "HH:mm")}-${format(
          new Date(item.reservation.end_date),
          "HH:mm"
        )}`,
      ]
        .filter(Boolean)
        .join(" ") +
      ` • ${price}${discount}`
    );
  }

  return `${prefix}${item?.item_type ?? ""} • ${price}${discount}`;
};
